'use client';

import React from 'react';
import { LucideIcon } from 'lucide-react';

interface AdminButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  icon?: LucideIcon;
  loading?: boolean;
}

export function AdminButton({ 
  children, 
  variant = 'primary', 
  size = 'md', 
  icon: Icon, 
  loading, 
  className = '', 
  disabled,
  ...props 
}: AdminButtonProps) {
  const baseStyles = "inline-flex items-center justify-center gap-2 font-black uppercase tracking-widest transition-all duration-300 rounded-xl disabled:opacity-50 disabled:pointer-events-none active:scale-95";
  
  const variants = {
    primary: "bg-soot text-text-inverse shadow-lg shadow-black/10 hover:bg-moss hover:shadow-xl hover:-translate-y-0.5",
    secondary: "bg-eucalyptus text-text-inverse shadow-lg shadow-eucalyptus/20 hover:bg-moss hover:shadow-xl hover:-translate-y-0.5",
    outline: "bg-transparent border-2 border-soot text-soot hover:bg-soot hover:text-text-inverse",
    ghost: "bg-transparent text-soot hover:bg-soot/5",
    danger: "bg-red-500 text-text-inverse hover:bg-red-600 shadow-lg shadow-red-500/20"
  };

  const sizes = {
    sm: "px-4 py-2 text-[9px]",
    md: "px-6 py-3 text-[10px]",
    lg: "px-8 py-4 text-[11px]"
  };

  return (
    <button 
      className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${className}`}
      {...props}
      disabled={Boolean(loading) || Boolean(disabled)}
    >
      {loading ? (
        <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
      ) : Icon ? (
        <Icon className="w-4 h-4" />
      ) : null}
      {children}
    </button>
  );
}
